<template>
  <div>
    <v-modal :short="isSuccess" @close="handleClose">
      <template slot="#post-creator">
        <action-body class="post-creator__container">
          <div v-if="isSuccess" class="p-3 pt-5 mt-3 container text-center">
            <v-icon
              icon="done_all"
              size="3rem"
              color="var(--brand-color-hex)"
              class="mt-5"
            />
            <h2 class="text-dark-blue mb-0 mt-1">Post Published!</h2>
            <p class="text-grey-70 mt-2">
              Your post was successfully published.
            </p>
          </div>
          <template v-else>
            <v-input
              v-model="title"
              flush
              placeholder="Post Title (optional)"
              class="post-title mx-2 pt-3"
            />
            <rich-text-editor
              placeholder="Write something..."
              class="px-2 pb-2"
              :content="content"
              @input="value => (content = value)"
              @uploading="value => (isUploading = value)"
            />
          </template>
        </action-body>
        <action-footer classes="w-100 d-flex post-creator__footer">
          <v-button ghost class="w-25 p-0" @click="handleClose">
            Close
          </v-button>
          <v-button
            v-if="!isSuccess"
            class="w-75 publish-button"
            :is-loading="isPosting"
            :disabled="isDisableSubmit"
            @click="handlePublishPost"
          >
            Publish
          </v-button>
        </action-footer>
      </template>
    </v-modal>

    <alert-dialog
      :actions="[
        {
          title: 'Cancel',
          callback: () => (this.showExitModal = false)
        },
        {
          title: 'Yes',
          callback: () => $emit('close')
        }
      ]"
      :show="showExitModal"
      title="Are you sure?"
      centered
      @close="() => (this.showExitModal = false)"
    >
      <p class="font-semi-bold">
        You have unsaved changes. Are you sure you want to leave?
      </p>
    </alert-dialog>
  </div>
</template>

<script>
import ApiClient from "@/lib/api-client"
import { isEmpty, isPresent } from "@/lib/utils"
import { get, set, unset } from "@/lib/local-storage"

import ActionBody from "@/components/modals/shared/ActionBody"
import ActionFooter from "@/components/modals/shared/ActionFooter"

import vInput from "@/components/forms/vInput"
import RichTextEditor from "@/components/RichTextEditor"

export default {
  components: {
    ActionBody,
    ActionFooter,
    vInput,
    RichTextEditor,
    AlertDialog: () => import("@/components/AlertDialog")
  },

  props: {
    messageThreadId: { type: [Number, String], required: true }
  },

  data() {
    return {
      title: null,
      content: null,
      isUploading: false,
      isPosting: false,
      isSuccess: false,
      debounce: null,
      showExitModal: false
    }
  },

  computed: {
    isDisableSubmit() {
      return this.isUploading || isEmpty(this.content)
    },

    isDirty() {
      return isPresent(this.title) || isPresent(this.content)
    },

    draftKey() {
      return `mvt-post-draft:{${this.messageThreadId}}`
    }
  },

  mounted() {
    const draftData = get(this.draftKey)

    if (draftData) {
      this.title = draftData.title
      this.content = draftData.content
    }
  },

  beforeDestroy() {
    if (!this.isDirty) {
      unset(this.draftKey)
    }
  },

  watch: {
    title() {
      this.handleSaveDraft()
    },

    content() {
      this.handleSaveDraft()
    }
  },

  methods: {
    handlePublishPost() {
      this.isPosting = true

      ApiClient.post(`/threads/${this.messageThreadId}/post`, {
        title: this.title,
        content: this.content
      })
        .then(res => {
          this.isSuccess = true
          this.handleClearDraft()
          this.$emit("posted", res.data.message_ts)
        })
        .catch(err => {
          this.$notify(err.response.data.message)
        })
        .finally(() => {
          this.isPosting = false
        })
    },

    handleSaveDraft() {
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        if (this.draftKey)
          set(this.draftKey, {
            title: this.title,
            content: this.content
          })
      }, 100)
    },

    handleClearDraft() {
      this.title = null
      this.content = null
      unset(this.draftKey)
    },

    handleClose() {
      if (this.isDirty) {
        this.showExitModal = true
        this.$router.push("#post-creator")
      } else {
        this.$emit("close")
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.post-creator__container {
  .tip-tap-editor {
    display: flex;
    justify-content: space-between;
    flex-direction: column;
  }

  .post-title input {
    font-size: $size--6;
    font-weight: bold;
    color: $color-ui--black;
  }
}
</style>
